import {
  Add,
  ArrowBack,
  Delete,
  Info,
  LogoutOutlined,
  Save,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { organizationsApi } from "api";
import { create_UUID, getTranslation, toDataUrl } from "common";
import { putPrioritiesOnServer } from "common/utilities";
import { ImagePicker } from "components";
import ConfirmDialog from "components/dialogs/ConfirmDialog";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import OrganizationLabelsEditor from "components/OrganizationLabelsEditor";
import OrganizationCalendarSettings from "components/organization-calendar/OrganizationCalendarSettings";
import SlaOrganizationSettingsPanel, {
  type SlaOrganizationSettingsHandle,
  type SlaSettingsSaveState,
} from "components/sla/SlaOrganizationSettingsPanel";
import SlaOrganizationUserPreferences, {
  type SlaOrganizationUserPreferencesHandle,
  type SlaPreferencesSaveState,
} from "components/sla/SlaOrganizationUserPreferences";
// Pannello digest+saluto delle Statistiche: sostituito qui da
// SlaOrganizationUserPreferences (fuso orario e "Notifiche SLA" dell'utente).
// import SlaUserPreferencesPanel from "components/sla/SlaUserPreferencesPanel";
import { OrganizationsContext } from "contexts";
import {
  useForm,
  useOrganizations,
  usePriorities,
  useSnackbar,
  useTranslations,
  useUser,
  useVersions,
} from "hooks";
import { Validation, isRequired, validate } from "hooks/useForm";
import { Priority } from "hooks/usePriorities";

const ORGANIZATIONS_ROUTE = "/dashboard/organizations";
const SETTINGS_TABS = ["priorities", "labels", "calendar", "sla"];
const DEFAULT_TAB = "priorities";
/**
 * Schede dismesse ancora raggiungibili da link salvati: "Notifiche SLA" e'
 * confluita in "Impostazioni SLA" (stessa pagina, sotto-sezione).
 */
const LEGACY_TABS: Record<string, string> = { "sla-notifications": "sla" };

/**
 * Vista **impostazioni dell'organizzazione** (`/dashboard/organizations/:organizationId/settings`).
 *
 * Sostituisce la modale di modifica che si apriva dall'ingranaggio della pagina
 * Organizzazioni (`OrganizationDialog` in modalita' modifica): ne conserva
 * tutto il contenuto — icona, nome, uscita dall'organizzazione, priorita',
 * etichette, calendario — con gli stessi id DOM, e aggiunge le impostazioni SLA
 * che prima stavano nelle Statistiche (interruttore di organizzazione e
 * notifiche SLA con il riepilogo giornaliero). La configurazione SLA per
 * modello (processo e attivita') vive nel designer del modello, sezione "SLA"
 * del pannello proprieta'.
 *
 * Stessa guardia della modale: solo l'amministratore dell'organizzazione
 * (`organization.administrator`); chiunque altro torna alla pagina
 * Organizzazioni. L'organizzazione si risolve dall'elenco dell'utente, non dal
 * solo id della rotta, cosi' un indirizzo digitato a mano non scavalca il ruolo.
 */
const OrganizationSettings: React.FC = () => {
  const translations = useTranslations();
  const t = (key: string) => getTranslation(translations, key);
  const navigate = useNavigate();
  const user = useUser();
  const { sendSnack } = useSnackbar();
  const { updateVersion } = useVersions();
  const { organizationId } = useParams<{ organizationId: string }>();
  const {
    state: { organizationsUserId },
    actions: { setSelectedOrganization },
  } = useContext(OrganizationsContext);
  const [organizations, refreshOrganizations] = useOrganizations();
  const organizationsLoaded = Boolean(user?.userId) && organizationsUserId === user?.userId;
  const organization = useMemo(
    () => organizations.find(({ id }) => String(id) === organizationId) ?? null,
    [organizations, organizationId],
  );

  // Scheda nell'URL (`?tab=sla`): i link aprono la scheda giusta.
  // `replace`: cambiare scheda non aggiunge voci alla cronologia, quindi il
  // comportamento del Back resta quello di prima.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const requestedTab = rawTab ? LEGACY_TABS[rawTab] ?? rawTab : null;
  const section = requestedTab && SETTINGS_TABS.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
  const setSection = (tab: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("tab", tab);
        // `model` era il modello aperto nella vecchia tabella SLA.
        next.delete("model");
        return next;
      },
      { replace: true },
    );
  const [saveLoading, setSaveLoading] = useState(false);
  // Scheda SLA: un solo "Salva" (`btn-save-organization`) per tutta la vista.
  // Il pannello SLA non rende il suo pulsante; la pagina ne legge lo stato e
  // chiama `save()` dal ref dopo l'eventuale PUT dell'identita'.
  const slaPanelRef = useRef<SlaOrganizationSettingsHandle>(null);
  const [slaSaveState, setSlaSaveState] = useState<SlaSettingsSaveState>({
    dirty: false,
    canSave: false,
    busy: false,
  });
  const handleSlaSaveState = useCallback((state: SlaSettingsSaveState) => setSlaSaveState(state), []);
  // "Notifiche SLA" dell'utente corrente (spostate da "Le mie attivita'"): bozza
  // nel blocco, PATCH dal "Salva" unico dopo il PUT SLA.
  const slaPreferencesRef = useRef<SlaOrganizationUserPreferencesHandle>(null);
  const [slaPreferencesState, setSlaPreferencesState] = useState<SlaPreferencesSaveState>({
    dirty: false,
    busy: false,
  });
  const handleSlaPreferencesState = useCallback(
    (state: SlaPreferencesSaveState) => setSlaPreferencesState(state),
    [],
  );
  // Ultima identita' (nome, immagine) letta o salvata: dirty-check delle schede
  // con il "Salva" unico (Priorita', SLA).
  const [identityBaseline, setIdentityBaseline] = useState({ name: "", icon: "" });
  // Ultime priorita' lette o salvate: dirty-check della scheda Priorita'.
  const [prioritiesBaseline, setPrioritiesBaseline] = useState<Priority[]>([]);
  const [exitFromOrganization, setExitFromOrganization] = useState(false);
  const [priorities, prioritiesLoading] = usePriorities(organization?.id ?? null);

  const validations: Validation[] = [
    ({ name }) =>
      isRequired(name) || {
        name: t("placeholders.nameisrequired.label"),
      },
  ];
  const { isValid, changeHandler, values, errors, touched, setValues } = useForm(
    { priorities: [], name: "", icon: "" },
    validations,
  );

  // Come la modale: i campi partono dall'organizzazione, le priorita' dal Server.
  useEffect(() => {
    if (organization) {
      setValues((current: any) => ({
        ...current,
        name: organization.name,
        icon: organization.icon || "",
      }));
      setIdentityBaseline({ name: organization.name, icon: organization.icon || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  useEffect(() => {
    if (!prioritiesLoading) {
      setValues((current: any) => ({ ...current, priorities }));
      setPrioritiesBaseline(priorities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritiesLoading, priorities]);

  // `?tab=sla-notifications` (link salvati): l'URL diventa `?tab=sla`.
  useEffect(() => {
    if (rawTab && LEGACY_TABS[rawTab]) setSection(LEGACY_TABS[rawTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab]);

  if (!organizationsLoaded) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  // Stessa guardia dell'ingranaggio (`OrganizationsHeader`): solo l'admin.
  if (!organization || !organization.administrator) {
    return <Navigate to={ORGANIZATIONS_ROUTE} replace />;
  }

  const goBack = () => navigate(ORGANIZATIONS_ROUTE);

  /**
   * PUT dell'identita'. `notify` falso quando segue un altro esito (scheda SLA);
   * `refresh` falso per rinviare la rilettura delle organizzazioni a fine
   * sequenza (la rilettura rimonta la pagina e con essa il pannello SLA).
   */
  const putIdentity = async (notify = true, refresh = true): Promise<boolean> => {
    try {
      const icon = values.icon ? await toDataUrl(values.icon) : null;
      const { data } = await organizationsApi.put(`${organization.id}`, {
        name: values.name,
        icon,
      });
      updateVersion(organization.id);
      if (notify) sendSnack({ message: `${data.responsemessage}`, type: "success" });
      setIdentityBaseline({ name: values.name, icon: values.icon || "" });
      if (refresh) await refreshOrganizations();
      return true;
    } catch {
      return false;
    }
  };

  const saveOrganization = async () => {
    setSaveLoading(true);
    try {
      await putIdentity();
    } finally {
      setSaveLoading(false);
    }
  };

  const identityDirty =
    values.name !== identityBaseline.name || (values.icon || "") !== identityBaseline.icon;

  /** PUT delle priorita' e rilettura; `false` al primo errore (snack di errore). */
  const putPriorities = async (): Promise<boolean> => {
    try {
      await putPrioritiesOnServer(values.priorities, organization.id);
      const { data } = await organizationsApi.get(`${organization.id}/priorities`);
      updateVersion(organization.id);
      changeHandler("priorities", data.priorities);
      setPrioritiesBaseline(data.priorities);
      return true;
    } catch {
      sendSnack({ message: t("generic.server.error"), type: "error" });
      return false;
    }
  };

  const prioritiesDirty = JSON.stringify(values.priorities ?? []) !== JSON.stringify(prioritiesBaseline);

  // Schede con un solo "Salva" (`btn-save-organization`) per tutta la vista:
  // identita' (se cambiata) e poi la sezione della scheda (se cambiata).
  const onSlaTab = section === "sla";
  const unifiedTab = onSlaTab || section === "priorities";
  /** Sezione della scheda da salvare dopo l'identita'. */
  // Scheda SLA: impostazioni di organizzazione (PUT) e preferenze dell'utente (PATCH).
  const sectionPending = onSlaTab
    ? slaSaveState.canSave || slaPreferencesState.dirty
    : section === "priorities" && prioritiesDirty;
  /** Sezione che impedisce il salvataggio (bozza non valida o lettura in corso). */
  const sectionBlocked = onSlaTab
    ? slaSaveState.busy ||
      slaPreferencesState.busy ||
      (slaSaveState.dirty && !slaSaveState.canSave)
    : section === "priorities" && prioritiesLoading;
  const sectionDirty = onSlaTab
    ? slaSaveState.dirty || slaPreferencesState.dirty
    : section === "priorities" && prioritiesDirty;

  /**
   * "Salva" unico delle schede Priorita' e SLA: identita' (se cambiata), poi la
   * sezione della scheda (se cambiata), in sequenza. Una sola attesa; al primo
   * errore si ferma (l'errore dell'identita' lo mostra l'interceptor, quello
   * della sezione la sezione stessa). Sulla scheda SLA la sequenza e'
   * identita' -> PUT impostazioni SLA -> PATCH preferenze dell'utente (solo le
   * parti cambiate); il messaggio di successo e' uno solo: quello del PUT SLA,
   * oppure quello del PATCH quando cambiano solo le preferenze.
   */
  const saveView = async () => {
    const pending = sectionPending;
    let identitySaved = false;
    setSaveLoading(true);
    try {
      if (identityDirty) {
        identitySaved = await putIdentity(!(onSlaTab && pending), false);
        if (!identitySaved) return;
      }
      if (!pending) return;
      if (onSlaTab) {
        const settingsPending = slaSaveState.canSave;
        if (settingsPending && slaPanelRef.current) {
          const settingsSaved = await slaPanelRef.current.save();
          if (!settingsSaved) return;
        }
        if (slaPreferencesState.dirty && slaPreferencesRef.current) {
          await slaPreferencesRef.current.save(!settingsPending);
        }
      } else {
        await putPriorities();
      }
    } finally {
      setSaveLoading(false);
      if (identitySaved) await refreshOrganizations();
    }
  };

  // `isValid` di useForm parte falso (nome vuoto) e `setValues` non lo ricalcola:
  // sulle schede con il "Salva" unico la validita' si calcola sui valori correnti.
  const identityValid = unifiedTab ? validate(validations, values).isValid : isValid;
  const saveDisabled =
    !identityValid ||
    saveLoading ||
    (unifiedTab && (sectionBlocked || (!identityDirty && !sectionPending)));

  const leaveOrganization = async () => {
    setSaveLoading(true);
    try {
      const { data } = await organizationsApi.delete(
        `/${organization.id}/users/${user?.userId}`,
      );
      sendSnack({ message: `${data.responsemessage}`, type: "success" });
      setSelectedOrganization(null);
      await refreshOrganizations();
      goBack();
    } catch {
    } finally {
      setSaveLoading(false);
    }
  };

  const updatePriority = (id: Priority["id"], patch: Partial<Priority>) => {
    changeHandler(
      "priorities",
      values.priorities.map((priority: Priority) =>
        priority.id === id ? { ...priority, ...patch } : priority,
      ),
    );
  };

  const fieldsetSx = {
    borderRadius: 2,
    borderColor: (theme: any) => theme.palette.divider,
    borderWidth: "1px",
    px: 3,
    py: 2,
  };

  const tabs: [string, string][] = [
    ["priorities", "generic.priorities.caption"],
    ["labels", "generic.labels.caption"],
    ["calendar", "calendar.settings.title"],
    ["sla", "sla.settings.title"],
  ];

  return (
    <Box
      data-testid="organization-settings-page"
      sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, overflow: "auto", height: "100%" }}
    >
      {/* Intestazione: Indietro e titolo come prima; a destra, all'altezza del
          nome, l'uscita dall'organizzazione (bordo destro allineato alla app bar,
          stesso px). Su schermi stretti la riga va a capo. */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1.5} sx={{ mb: 2 }}>
        <FlowentiButton
          id="btn-organization-settings-back"
          size="small"
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={goBack}
        >
          {t("generic.back.caption")}
        </FlowentiButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t("settings.name")}
          </Typography>
          <Typography variant="h6" fontWeight={800} noWrap>
            {organization.name}
          </Typography>
        </Box>
        <Box data-testid="organization-membership-actions" sx={{ ml: "auto", flexShrink: 0 }}>
          <Button
            id="btn-cancel-organization"
            variant="outlined"
            color="error"
            size="small"
            disabled={saveLoading}
            startIcon={<LogoutOutlined />}
            onClick={() => setExitFromOrganization(true)}
            sx={{
              borderRadius: 5,
              fontWeight: 700,
              whiteSpace: "nowrap",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              borderColor: (theme) => alpha(theme.palette.error.main, 0.5),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.16),
                borderColor: "error.main",
              },
            }}
          >
            {t("generic.exit_organization.caption")}
          </Button>
        </Box>
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={4}>
          {/* Immagine a meta' della dimensione di prima: si stringe solo qui il
              contenitore e l'`ImagePicker` condiviso (usato anche dalle modali
              Gruppo/Ruolo/Robot) resta invariato. */}
          <Box data-testid="organization-image-settings" sx={{ width: 140, flexShrink: 0 }}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 1,
                minHeight: 120,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                "& > div": { minWidth: 0, maxWidth: "100%" },
                "& .MuiStack-root": { top: 4, right: 4, gap: 0.5, "& > :not(style) ~ :not(style)": { ml: 0 } },
                "& .MuiIconButton-root": { p: 0.5 },
                "& .MuiIconButton-root svg": { fontSize: 16 },
              }}
            >
              <ImagePicker
                photoUrl={values?.icon || ""}
                relativePath={organization.icon || ""}
                setPhotoUrl={(value) => changeHandler("icon", value)}
              />
            </Box>
          </Box>
          <Grid flex={1} spacing={3} container data-testid="organization-identity-settings">
            <Grid item xs={12}>
              <TextField
                id="organizations-dialog"
                variant="outlined"
                fullWidth
                size="small"
                label={t("placeholders.organization.name.label")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                value={values?.name || ""}
                onChange={({ target }) => changeHandler("name", target.value)}
              />
            </Grid>
          </Grid>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2} sx={{ width: "100%" }}>
          <Tabs
            value={section}
            onChange={(_, value) => setSection(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label={organization.name}
          >
            {tabs.map(([value, key]) => (
              <Tab
                key={value}
                value={value}
                id={`organization-tab-${value}`}
                aria-controls={`organization-panel-${value}`}
                label={t(key)}
              />
            ))}
          </Tabs>

          <Box role="tabpanel" id="organization-panel-priorities" aria-labelledby="organization-tab-priorities" hidden={section !== "priorities"}>
            <TableContainer component="fieldset" sx={fieldsetSx}>
              <FormLabel component="legend" sx={{ px: 1, fontWeight: 800, letterSpacing: 1 }}>
                {t("generic.priorities.caption")}
              </FormLabel>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("register.registration.name.label")}</TableCell>
                    <TableCell>{t("generic.order.caption")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!prioritiesLoading &&
                    values?.priorities &&
                    values.priorities
                      .filter((_: Priority) => !_.deleted)
                      .map(({ idorder, name, id, candelete }: Priority) => (
                        <TableRow key={`priority-${id}`}>
                          <TableCell sx={{ width: "48%" }}>
                            <TextField
                              id={`priorities-name-${id}`}
                              variant="standard"
                              size="small"
                              value={name}
                              onChange={({ target }) => updatePriority(id, { name: target.value })}
                            />
                          </TableCell>
                          <TableCell sx={{ width: "32%" }}>
                            <TextField
                              id={`priorities-order-${id}`}
                              variant="standard"
                              size="small"
                              value={idorder}
                              type="number"
                              inputProps={{ min: 0 }}
                              onChange={({ target }) => updatePriority(id, { idorder: Number(target.value) })}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                !candelete &&
                                t("generic.cannot_delete_priority_assigned_to_activity.caption")
                              }
                            >
                              <span>
                                <IconButton
                                  disabled={!candelete}
                                  onClick={() => updatePriority(id, { deleted: true })}
                                  size="small"
                                >
                                  {!candelete ? <Info /> : <Delete />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Stack justifyContent="center" alignItems="center">
                        <FlowentiButton
                          id="add-priorities"
                          onClick={() => {
                            changeHandler("priorities", [
                              ...values.priorities,
                              {
                                id: create_UUID(),
                                new: true,
                                name: "",
                                idorder: 0,
                                candelete: true,
                                idorganization: organization.id,
                              },
                            ]);
                          }}
                          startIcon={<Add />}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        >
                          {t("generic.add.caption")}
                        </FlowentiButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* Nessun "Salva" proprio: le priorita' si salvano col "Salva" unico
                della pagina (`btn-save-organization`), insieme all'identita'. */}
          </Box>

          <Box role="tabpanel" id="organization-panel-labels" aria-labelledby="organization-tab-labels" hidden={section !== "labels"}>
            <Box component="fieldset" sx={fieldsetSx}>
              <FormLabel component="legend" sx={{ px: 1, fontWeight: 800, letterSpacing: 1 }}>
                {t("generic.labels.caption")}
              </FormLabel>
              <OrganizationLabelsEditor organizationId={organization.id} />
            </Box>
          </Box>

          <Box role="tabpanel" id="organization-panel-calendar" aria-labelledby="organization-tab-calendar" hidden={section !== "calendar"}>
            <OrganizationCalendarSettings organizationId={organization.id} />
          </Box>

          {/* Impostazioni SLA di organizzazione (prima nelle Statistiche): montate
              solo quando la scheda e' aperta, cosi' la vista non legge le rotte
              SLA finche' l'utente non le chiede. */}
          <Box role="tabpanel" id="organization-panel-sla" aria-labelledby="organization-tab-sla" hidden={section !== "sla"}>
            {section === "sla" && (
              <Stack spacing={2}>
                <Stack spacing={2} component="fieldset" sx={fieldsetSx}>
                  {/* Interruttore in alto, poi le impostazioni di organizzazione.
                      Il solo "Salva" della vista e' `btn-save-organization`
                      (in fondo alla pagina): salva identita' e SLA. Gli SLA
                      per modello si configurano nel designer. */}
                  <SlaOrganizationSettingsPanel
                    ref={slaPanelRef}
                    idorganization={organization.id}
                    isAdmin={organization.administrator}
                    showOrganizationFields
                    hideSaveButton
                    onSaveStateChange={handleSlaSaveState}
                  />
                </Stack>
                {/* Fuso orario e "Notifiche SLA" dell'utente corrente per
                    questa organizzazione (spostati da "Le mie attivita'").
                    Nessun autosalvataggio: le preferenze si salvano col "Salva"
                    della pagina, dopo il PUT delle impostazioni sopra. */}
                <Box
                  component="fieldset"
                  sx={fieldsetSx}
                  id="organization-panel-sla-notifications"
                  data-testid="organization-sla-notifications"
                >
                  <SlaOrganizationUserPreferences
                    ref={slaPreferencesRef}
                    idorganization={organization.id}
                    onSaveStateChange={handleSlaPreferencesState}
                  />
                </Box>
                {/* Pannello precedente (digest e saluto), non piu' usato qui.
                <Box
                  component="fieldset"
                  sx={fieldsetSx}
                  id="organization-panel-sla-notifications"
                  data-testid="organization-sla-notifications"
                >
                  <SlaUserPreferencesPanel idorganization={organization.id} />
                </Box>
                */}
              </Stack>
            )}
          </Box>
        </Stack>

        {/* "Salva" unico della pagina, in fondo alla vista (non accanto al nome):
            identita' e, su Priorita'/SLA, anche la sezione della scheda.
            Allineato a sinistra (sulla scheda SLA subito sotto il "Digest
            giornaliero"): a destra finiva sotto il pulsante flottante
            dell'assistente AI. */}
        <Box data-testid="organization-settings-save" sx={{ mt: 3, display: "flex", justifyContent: "flex-start" }}>
          <FlowentiButton
            id="btn-save-organization"
            variant="contained"
            disabled={saveDisabled}
            data-dirty={unifiedTab ? String(identityDirty || sectionDirty) : undefined}
            startIcon={<Save />}
            size="small"
            onClick={unifiedTab ? saveView : saveOrganization}
          >
            {t("pm.button.save")}
          </FlowentiButton>
        </Box>
      </Paper>

      <ConfirmDialog
        open={exitFromOrganization}
        onClose={() => setExitFromOrganization(false)}
        title={t("generic.exit_organization.caption")}
        content={t("pm.confirm.leave_organization")}
        cancelText={t("generic.button.cancel")}
        confirmText={t("generic.button.confirm")}
        onConfirm={() => {
          if (saveLoading) return;
          setExitFromOrganization(false);
          leaveOrganization();
        }}
      />
    </Box>
  );
};

export default OrganizationSettings;
